import { Outlet, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { oktaConfig, isPublicPath } from '../../config/auth';
import { useOktaAuthReady } from '../../hooks/useOktaAuthReady';

/**
 * Module-level guard. React Strict Mode double-invokes effects in dev, which
 * would otherwise call `signInWithRedirect` twice and blow up Okta's state.
 * This boolean is flipped the first time we trigger a redirect and never
 * reset outside of tests (the real page is about to navigate away anyway).
 */
let oktaLoginRedirectIssued = false;

/** Test-only hook to reset the module-level redirect guard between suites. */
export function resetOktaLoginRedirectGuardForTests() {
  oktaLoginRedirectIssued = false;
}

/**
 * Layout route that gates its children behind Okta auth.
 *
 *  - public paths render unconditionally,
 *  - auth that hasn't resolved yet renders a Loading block,
 *  - authenticated users get the `<Outlet />`,
 *  - everyone else is redirected into Okta's hosted sign-in.
 *
 * When redirecting we store the current `pathname + search + hash` via
 * `setOriginalUri` so `restoreOriginalUri` can return the user there after
 * callback.
 */
const RequireAuth = () => {
  const location = useLocation();
  const { oktaAuth, isAuthResolved, isAuthenticated } = useOktaAuthReady();

  const isPublic = isPublicPath(location.pathname);

  useEffect(() => {
    if (isPublic) return;
    if (!isAuthResolved) return;
    if (isAuthenticated) return;
    if (oktaLoginRedirectIssued) return;

    oktaLoginRedirectIssued = true;
    const originalUri = `${location.pathname}${location.search}${location.hash}`;
    oktaAuth.setOriginalUri(originalUri);
    oktaAuth.signInWithRedirect({ scopes: [...oktaConfig.scopes] });
  }, [
    isPublic,
    isAuthResolved,
    isAuthenticated,
    oktaAuth,
    location.pathname,
    location.search,
    location.hash,
  ]);

  if (isPublic) return <Outlet />;
  if (!isAuthResolved) return <div className="auth-loading">Loading...</div>;
  if (isAuthenticated) return <Outlet />;

  return <div className="auth-loading">Redirecting to sign in...</div>;
};

export default RequireAuth;
